import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '@/components/Sidebar';
import Header from '@/components/Header';
import { useTheme } from '@/contexts/ThemeContext';
import { FileText, Printer, Download, Calendar, MapPin, DollarSign, Building, User, Search, CreditCard, ArrowUpRight, CheckCircle, XCircle, Clock, ExternalLink, AlertTriangle } from 'lucide-react';
import '@/styles/commandDeck.css';

const InvoicesPrintable = ({ onLogout }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('invoices');
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const jobsPerPage = 5;
  const [modalTransactions, setModalTransactions] = useState([]);
  const [modalTransactionsLoading, setModalTransactionsLoading] = useState(false);
  const [cancellationInvoices, setCancellationInvoices] = useState([]);
  const [cancellationInvoicesLoading, setCancellationInvoicesLoading] = useState(false);

  useEffect(() => {
    fetchCompletedJobs();
  }, [searchTerm]);  // Refetch when search changes

  useEffect(() => {
    if (activeTab === "transactions" && transactions.length === 0) {
      fetchTransactions();
    }
    if (activeTab === "cancellation_fees" && cancellationInvoices.length === 0) {
      fetchCancellationInvoices();
    }
  }, [activeTab]);

  const fetchCancellationInvoices = async () => {
    setCancellationInvoicesLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/cancellation-invoices`,
        { params: { user_email: user.email } }
      );
      setCancellationInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch cancellation invoices:', error);
    } finally {
      setCancellationInvoicesLoading(false);
    }
  };


  const fetchStripeTimeline = async (jobId, invoiceTransactions = []) => {
    setModalTransactionsLoading(true);
    try {
      // 1. Fetch comprehensive job transactions (searches Stripe + payment_transactions)
      // 2. Fetch precise amounts for invoice-record charge IDs (extra payments etc.)
      // Then merge and deduplicate
      const invoiceChargeIds = invoiceTransactions.map(t => t.id).filter(Boolean);

      const [jobTxnRes, stripeTlRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/job/${jobId}/transactions`).catch(() => null),
        invoiceChargeIds.length > 0
          ? axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/stripe-timeline`, { charge_ids: invoiceChargeIds }).catch(() => null)
          : null,
      ]);

      // Start with job transactions (filtered to succeeded charges)
      const allJobTxns = jobTxnRes?.data?.transactions || [];
      const merged = allJobTxns
        .filter(t => t.status === 'succeeded' && (t.type === 'charge' || t.type === 'payment' || t.captured))
        .map(t => ({
          id: t.id || t.charge_id,
          charge_id: t.charge_id || t.id,
          date_formatted: t.date_formatted,
          amount: t.amount,
        }));

      // Merge in stripe-timeline results (precise amounts for invoice-record charges)
      const seenIds = new Set(merged.map(t => t.id));
      const stripeTxns = stripeTlRes?.data?.transactions || [];
      for (const txn of stripeTxns) {
        if (txn.error) continue;
        // If this charge ID or its parent PI is already present, replace with precise data
        const existingIdx = merged.findIndex(m => m.id === txn.id || m.charge_id === txn.charge_id);
        if (existingIdx >= 0) {
          merged[existingIdx] = txn;
        } else if (!seenIds.has(txn.id)) {
          merged.push(txn);
          seenIds.add(txn.id);
        }
      }

      if (merged.length > 0) {
        setModalTransactions(merged);
      } else {
        // Fall back to invoice-record data
        setModalTransactions(invoiceTransactions.map(t => ({
          id: t.id,
          date_formatted: t.description || formatDate(t.date),
          amount: t.amount,
        })));
      }
    } catch (error) {
      console.error("Failed to fetch Stripe timeline:", error);
      setModalTransactions(invoiceTransactions.map(t => ({
        id: t.id,
        date_formatted: t.description || formatDate(t.date),
        amount: t.amount,
      })));
    } finally {
      setModalTransactionsLoading(false);
    }
  };

  const fetchCompletedJobs = async (pageNum = 1) => {
    try {
      const isLoadingMore = pageNum > 1;
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/invoices/completed-jobs`,
        {
          params: {
            user_email: user.email,
            page: pageNum,
            limit: jobsPerPage,
            search: searchTerm || undefined
          }
        }
      );

      if (response.data.success) {
        const newJobs = response.data.invoices || [];

        if (isLoadingMore) {
          setJobs(prev => [...prev, ...newJobs]);
        } else {
          setJobs(newJobs);
        }

        // Always update page number after successful fetch
        setPage(pageNum);

        // Check if there are more jobs
        setHasMore(newJobs.length === jobsPerPage);
      }
    } catch (error) {
      console.error('Failed to fetch completed jobs:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };


  const fetchTransactions = async (pageNum = 1) => {
    try {
      setTransactionsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/transactions`,
        { params: { user_email: user.email, page: pageNum, limit: 10 } }
      );
      if (response.data.success) {
        if (pageNum === 1) {
          setTransactions(response.data.transactions || []);
        } else {
          setTransactions(prev => [...prev, ...response.data.transactions]);
        }
        setHasMoreTransactions(response.data.has_more);
        setTransactionsPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleLoadMore = () => {
    fetchCompletedJobs(page + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  // --- Command Deck visual tokens -------------------------------------------
  const sub = 'rgba(255,255,255,.6)';
  const faint = 'rgba(255,255,255,.45)';
  const hair = 'rgba(255,255,255,.09)';
  const cardBg = 'rgba(255,255,255,.045)';

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} className="print:hidden" />
      <div className="print:hidden"><Header onLogout={onLogout} /></div>

      <div className="cd-root sidebar-content pt-16 lg:pt-20 print:hidden">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main">

          {/* Page Header */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 22 }}>
            <div>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#67C6B4' }}>BILLING</div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, letterSpacing: '-1px', color: '#fff', marginTop: 8 }}>Invoices &amp; payments</div>
            </div>
            <button
              type="button"
              aria-label="Notifications"
              style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', flex: '0 0 auto' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
              <span style={{ position: 'absolute', top: 11, right: 12, width: 8, height: 8, borderRadius: 999, background: '#28C0D0', border: '2px solid #0C1120' }} />
            </button>
          </div>

          {/* Tabs + Search */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
            <div className="cd-tabs">
              <button type="button" onClick={() => setActiveTab("invoices")} className={`cd-tab ${activeTab === "invoices" ? "cd-tab-active" : ""}`}>
                <FileText className="w-[15px] h-[15px]" /> Invoices
              </button>
              <button type="button" onClick={() => setActiveTab("transactions")} className={`cd-tab ${activeTab === "transactions" ? "cd-tab-active" : ""}`}>
                <CreditCard className="w-[15px] h-[15px]" /> All transactions
              </button>
              <button type="button" onClick={() => setActiveTab("cancellation_fees")} className={`cd-tab ${activeTab === "cancellation_fees" ? "cd-tab-active" : ""}`}>
                <AlertTriangle className="w-[15px] h-[15px]" /> Cancellation fees
              </button>
            </div>
            {activeTab === "invoices" && (
              <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 360 }}>
                <Search className="w-[17px] h-[17px]" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#A8AEBC' }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by job, owner, or ID..."
                  className="cd-input-plain"
                  style={{ width: '100%', padding: '11px 14px 11px 40px', border: '1px solid rgba(255,255,255,.12)', borderRadius: 12, background: 'rgba(255,255,255,.04)', fontFamily: "'Work Sans', sans-serif", fontSize: 13.5, color: '#fff', outline: 'none' }}
                />
              </div>
            )}
          </div>

          {activeTab === "invoices" && (
            <>
              {searchTerm && (
                <p style={{ fontSize: 12, color: faint, marginBottom: 14 }}>Searching for: "{searchTerm}"</p>
              )}

              {/* Jobs List */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: '56px 0' }}>
                  <div className="inline-block animate-spin rounded-full h-12 w-12" style={{ borderBottom: '2px solid #28C0D0' }}></div>
                  <p style={{ marginTop: 16, color: sub }}>Loading invoices...</p>
                </div>
              ) : jobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 0', background: cardBg, border: `1px solid ${hair}`, borderRadius: 16 }}>
                  <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: faint }} />
                  <p style={{ fontSize: 18, fontWeight: 600, color: sub }}>No completed jobs yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {jobs.map((job) => {
                    const isClient = job.invoice_type === 'client';
                    const status = job.payment_status;
                    const iconTone = status === 'paid_in_full'
                      ? { bg: 'rgba(103,198,180,.16)', fg: '#67C6B4' }
                      : status === 'awaiting_payment'
                        ? { bg: 'rgba(37,99,235,.18)', fg: '#60A5FA' }
                        : { bg: 'rgba(120,40,124,.2)', fg: '#C98AD4' };
                    const amountLabel = isClient
                      ? (status === 'partially_paid' ? 'Paid so far' : 'Total paid')
                      : (status === 'awaiting_payment' ? 'Net to receive' : 'Net received');
                    return (
                      <div
                        key={job.job_id}
                        className="cd-card cd-col-mobile"
                        style={{ alignItems: 'center', gap: 18, background: cardBg, border: `1px solid ${hair}`, borderRadius: 16, padding: '18px 22px' }}
                      >
                        <span style={{ width: 46, height: 46, borderRadius: 12, background: iconTone.bg, color: iconTone.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                          {isClient ? <Building className="w-[22px] h-[22px]" /> : <User className="w-[22px] h-[22px]" />}
                        </span>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                            <span className="cd-display" style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>
                              {job.title}
                              {job.crew_role && (
                                <span style={{ fontWeight: 500, color: faint, fontSize: 13, marginLeft: 6 }}>
                                  ({job.crew_role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())})
                                </span>
                              )}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: isClient ? '#C98AD4' : '#28C0D0', background: isClient ? 'rgba(120,40,124,.2)' : 'rgba(40,192,208,.14)', padding: '3px 9px', borderRadius: 999 }}>
                              {isClient ? 'Job owner' : 'Crew member'}
                            </span>
                          </div>
                          <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)', marginTop: 4 }}>
                            <span className="cd-mono">#{job.invoice_id || job.mysql_job_id || job.job_id?.substring(0, 8)}</span>
                            {' · '}{formatDate(job.date)}{' · '}{job.location || 'N/A'}
                          </div>
                        </div>

                        <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                          <div style={{ fontSize: 11, color: faint }}>{amountLabel}</div>
                          <div className="cd-display" style={{ fontWeight: 800, fontSize: 22, color: '#fff', letterSpacing: '-.4px', lineHeight: 1.1 }}>
                            {isClient ? formatCurrency(job.total_paid) : formatCurrency(job.net_received)}
                            {isClient && status === 'partially_paid' && job.total_due && (
                              <span style={{ fontSize: 12, fontWeight: 500, color: faint }}> of {formatCurrency(job.total_due)}</span>
                            )}
                          </div>
                          {status === 'paid_in_full' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '3px 8px', borderRadius: 999, marginTop: 4 }}>
                              <CheckCircle className="w-[10px] h-[10px]" />Paid in full
                            </span>
                          )}
                          {status === 'partially_paid' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: '#FCD34D', background: 'rgba(180,83,9,.2)', padding: '3px 8px', borderRadius: 999, marginTop: 4 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 999, background: '#FCD34D' }} />Partially paid
                            </span>
                          )}
                          {status === 'awaiting_payment' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: '#60A5FA', background: 'rgba(37,99,235,.18)', padding: '3px 8px', borderRadius: 999, marginTop: 4 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 999, background: '#60A5FA' }} />Awaiting payment
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            setSelectedJob(job); setModalTransactions([]);
                            setShowInvoice(true);
                          }}
                          className="cd-btn"
                          style={{ padding: '11px 16px', fontSize: 12.5, flex: '0 0 auto' }}
                        >
                          <FileText className="w-[15px] h-[15px]" />
                          View
                        </button>
                      </div>
                    );
                  })}

                  {/* Load More Button */}
                  {hasMore && !loading && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="cd-btn"
                        style={{ padding: '13px 28px', fontSize: 13.5, opacity: loadingMore ? 0.5 : 1 }}
                      >
                        {loadingMore ? (
                          <>
                            <div className="inline-block animate-spin rounded-full h-5 w-5" style={{ borderBottom: '2px solid #fff' }}></div>
                            <span>Loading...</span>
                          </>
                        ) : (
                          <>
                            <span>Load more…</span>
                            <span style={{ fontSize: 12, opacity: 0.6 }}>({jobsPerPage} more)</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Transactions Tab */}
          {activeTab === "transactions" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {transactionsLoading ? (
                <div style={{ textAlign: 'center', padding: '56px 0' }}>
                  <div className="inline-block animate-spin rounded-full h-12 w-12" style={{ borderBottom: '2px solid #28C0D0' }}></div>
                  <p style={{ marginTop: 16, color: sub }}>Loading transactions...</p>
                </div>
              ) : transactions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 0', background: cardBg, border: `1px solid ${hair}`, borderRadius: 16 }}>
                  <CreditCard className="w-16 h-16 mx-auto mb-4" style={{ color: faint }} />
                  <p style={{ fontSize: 18, fontWeight: 600, color: sub }}>No transactions found</p>
                  <p style={{ fontSize: 13, color: faint, marginTop: 8 }}>Your payment transactions will appear here</p>
                </div>
              ) : (
                <>
                  {transactions.map((txn) => {
                    const txTone = txn.status === 'succeeded'
                      ? { bg: 'rgba(103,198,180,.16)', fg: '#67C6B4' }
                      : txn.status === 'pending'
                        ? { bg: 'rgba(180,83,9,.2)', fg: '#FCD34D' }
                        : { bg: 'rgba(248,113,113,.16)', fg: '#F87171' };
                    return (
                      <div key={txn.charge_id} className="cd-card" style={{ flexDirection: 'column', alignItems: 'stretch', background: cardBg, border: `1px solid ${hair}`, borderRadius: 16, padding: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <span style={{ width: 46, height: 46, borderRadius: 12, background: txTone.bg, color: txTone.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                              {txn.status === 'succeeded' ? <CheckCircle className="w-[22px] h-[22px]" /> : txn.status === 'pending' ? <Clock className="w-[22px] h-[22px]" /> : <XCircle className="w-[22px] h-[22px]" />}
                            </span>
                            <div>
                              <p className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>{txn.description || 'Payment'}</p>
                              <p style={{ fontSize: 12.5, color: faint }}>{formatDate(txn.created)}</p>
                              {txn.payment_method_details && (
                                <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,.4)', marginTop: 2 }}>
                                  {txn.payment_method_details.card?.brand?.toUpperCase()} ****{txn.payment_method_details.card?.last4}
                                </p>
                              )}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p className="cd-display" style={{ fontWeight: 800, fontSize: 20, color: txn.type === 'refund' ? '#F87171' : '#fff', letterSpacing: '-.4px' }}>
                              {txn.type === 'refund' ? '-' : ''}{formatCurrency(txn.amount)}
                            </p>
                            <span style={{ display: 'inline-block', marginTop: 4, padding: '3px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: txTone.fg, background: txTone.bg }}>
                              {txn.status?.charAt(0).toUpperCase() + txn.status?.slice(1)}
                            </span>
                          </div>
                        </div>
                        {/* Job Details */}
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${hair}` }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12 }} className="sm:grid-cols-4">
                            <div>
                              <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: faint, marginBottom: 3 }}>Job</p>
                              <p className="truncate" style={{ fontWeight: 700, color: '#fff', fontSize: 13.5 }}>{txn.job_title || txn.description || "N/A"}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: faint, marginBottom: 3 }}>Job date</p>
                              <p style={{ color: sub, fontSize: 13.5 }}>{txn.job_date ? formatDate(txn.job_date) : "N/A"}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: faint, marginBottom: 3 }}>Transaction</p>
                              <p style={{ color: sub, fontSize: 13.5 }}>{formatDate(txn.created)}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: faint, marginBottom: 3 }}>Stripe ID</p>
                              <p className="cd-mono truncate" style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>{txn.charge_id}</p>
                            </div>
                          </div>
                          {txn.job_id && (
                            <button type="button" onClick={() => navigate(`/hire-crew?action=view_details&job_id=${txn.job_id}`)} className="cd-btn cd-btn-primary" style={{ marginTop: 14, padding: '9px 16px', fontSize: 12.5 }}>
                              <ExternalLink className="w-[15px] h-[15px]" />View job
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {hasMoreTransactions && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={() => fetchTransactions(transactionsPage + 1)}
                        disabled={transactionsLoading}
                        className="cd-btn"
                        style={{ padding: '13px 28px', fontSize: 13.5, opacity: transactionsLoading ? 0.5 : 1 }}
                      >
                        Load more transactions
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Cancellation Fees Tab */}
          {activeTab === "cancellation_fees" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {cancellationInvoicesLoading ? (
                <div style={{ textAlign: 'center', padding: '56px 0' }}>
                  <div className="inline-block animate-spin rounded-full h-12 w-12" style={{ borderBottom: '2px solid #F87171' }}></div>
                  <p style={{ marginTop: 16, color: sub }}>Loading cancellation invoices...</p>
                </div>
              ) : cancellationInvoices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 0', background: cardBg, border: `1px solid ${hair}`, borderRadius: 16 }}>
                  <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#67C6B4' }} />
                  <p style={{ fontSize: 18, fontWeight: 600, color: sub }}>No cancellation fees</p>
                  <p style={{ fontSize: 13, color: faint, marginTop: 8 }}>You have no cancellation fee invoices</p>
                </div>
              ) : (
                <>
                  {cancellationInvoices.map((invoice) => {
                    const isPaid = invoice.status === 'paid' || invoice.status === 'waived';
                    const isOwner = invoice.recipient_type === 'owner';
                    const statusLabel = invoice.status === 'waived' ? 'WAIVED' : isPaid ? 'PAID' : 'UNPAID';
                    const tone = isPaid ? { fg: '#67C6B4', bg: 'rgba(103,198,180,.16)', border: 'rgba(103,198,180,.25)' } : { fg: '#F87171', bg: 'rgba(248,113,113,.16)', border: 'rgba(248,113,113,.25)' };

                    return (
                      <div key={invoice.id} className="cd-card" style={{ flexDirection: 'column', alignItems: 'stretch', background: cardBg, border: `1px solid ${tone.border}`, borderRadius: 16, padding: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ width: 42, height: 42, borderRadius: 12, background: tone.bg, color: tone.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                              {isPaid ? <CheckCircle className="w-[20px] h-[20px]" /> : <AlertTriangle className="w-[20px] h-[20px]" />}
                            </span>
                            <div>
                              <p className="cd-mono" style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{invoice.invoice_number}</p>
                              <p style={{ fontSize: 11.5, color: faint }}>
                                {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                              </p>
                            </div>
                          </div>
                          <span style={{ padding: '4px 11px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: tone.fg, background: tone.bg }}>
                            {statusLabel}
                          </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: sub }}>
                            <FileText className="w-4 h-4" style={{ color: faint }} />
                            <span style={{ fontWeight: 500 }}>{invoice.job_title}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: faint }}>
                            <Calendar className="w-[14px] h-[14px]" />
                            <span>Job date: {invoice.job_date ? new Date(invoice.job_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                          </div>
                        </div>

                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${hair}` }}>
                          {isOwner ? (
                            <>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 }}>
                                <div>
                                  <p style={{ fontSize: 11.5, color: faint }}>Original charge</p>
                                  <p className="cd-display" style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>${(invoice.original_charge || 0).toFixed(2)}</p>
                                </div>
                                <div>
                                  <p style={{ fontSize: 11.5, color: faint }}>Refund issued</p>
                                  <p className="cd-display" style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>${(invoice.refund_amount || 0).toFixed(2)}</p>
                                </div>
                                <div>
                                  <p style={{ fontSize: 11.5, color: faint }}>Retained by Shootzu</p>
                                  <p className="cd-display" style={{ fontWeight: 800, fontSize: 15, color: tone.fg }}>${(invoice.total_amount_inc_gst || 0).toFixed(2)}</p>
                                </div>
                              </div>
                              <p style={{ fontSize: 11, color: faint, marginTop: 8 }}>
                                Retained includes ${(invoice.fee_amount_ex_gst || 0).toFixed(2)} fee (7%) + ${(invoice.gst_amount || 0).toFixed(2)} GST (10%)
                              </p>
                            </>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 }}>
                              <div>
                                <p style={{ fontSize: 11.5, color: faint }}>Fee (7%)</p>
                                <p className="cd-display" style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>${(invoice.fee_amount_ex_gst || 0).toFixed(2)}</p>
                              </div>
                              <div>
                                <p style={{ fontSize: 11.5, color: faint }}>GST (10%)</p>
                                <p className="cd-display" style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>${(invoice.gst_amount || 0).toFixed(2)}</p>
                              </div>
                              <div>
                                <p style={{ fontSize: 11.5, color: faint }}>Total</p>
                                <p className="cd-display" style={{ fontWeight: 800, fontSize: 15, color: tone.fg }}>${(invoice.total_amount_inc_gst || 0).toFixed(2)}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {invoice.pdf_path && (
                          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${hair}` }}>
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/download-cancellation/${invoice.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: '#28C0D0' }}
                            >
                              <Download className="w-[15px] h-[15px]" /> Download PDF
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Printable Tax Invoice Modal */}
      {showInvoice && selectedJob && (
        <div className="cd-modal-overlay" style={{ zIndex: 200 }}>
          <div className="cd-modal-card" style={{ maxWidth: 600 }}>

            {/* Header */}
            <div style={{ position: 'relative', padding: '24px 28px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
              <button
                type="button"
                onClick={() => setShowInvoice(false)}
                className="cd-modal-close"
                aria-label="Close"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                <img src="/assets/shootzu-logo-white.png" alt="Shootzu" style={{ height: 28, width: 'auto' }} />
                {selectedJob.payment_status === 'paid_in_full' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '5px 11px', borderRadius: 999 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: '#67C6B4' }} />Paid in full
                  </span>
                )}
                {selectedJob.payment_status === 'partially_paid' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#FCD34D', background: 'rgba(180,83,9,.2)', padding: '5px 11px', borderRadius: 999 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: '#FCD34D' }} />Partially paid
                  </span>
                )}
                {selectedJob.payment_status === 'awaiting_payment' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#60A5FA', background: 'rgba(37,99,235,.18)', padding: '5px 11px', borderRadius: 999 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: '#60A5FA' }} />Awaiting payment
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 20, flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div className="cd-display" style={{ fontWeight: 800, fontSize: 22, color: '#fff', letterSpacing: '-.5px' }}>Tax invoice</div>
                  <div className="cd-mono" style={{ fontSize: 12, color: faint, marginTop: 3 }}>
                    #{selectedJob.invoice_id || selectedJob.mysql_job_id || selectedJob.job_id?.substring(0, 8)} · issued {formatDate(selectedJob.payment_date)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11.5, color: faint }}>
                    {selectedJob.invoice_type === 'client'
                      ? (selectedJob.payment_status === 'partially_paid' ? 'Amount paid' : 'Total paid')
                      : 'You received'}
                  </div>
                  <div className="cd-display" style={{ fontWeight: 800, fontSize: 26, color: '#fff', letterSpacing: '-.6px' }}>
                    {selectedJob.invoice_type === 'client'
                      ? formatCurrency(selectedJob.total_paid)
                      : formatCurrency(selectedJob.net_received)}
                  </div>
                  {selectedJob.invoice_type === 'client' && selectedJob.payment_status === 'partially_paid' && selectedJob.total_due && (
                    <div style={{ fontSize: 11.5, color: faint, marginTop: 2 }}>of {formatCurrency(selectedJob.total_due)} total</div>
                  )}
                </div>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '22px 28px' }}>

              {/* From / Billed to */}
              <div className="cd-grid-2c" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: 'rgba(255,255,255,.4)', marginBottom: 7 }}>FROM (CREW MEMBER)</div>
                  <div style={{ fontSize: 13.5, color: '#fff', fontWeight: 600 }}>{selectedJob.crew_name}</div>
                  <div style={{ fontSize: 12.5, color: sub, lineHeight: 1.5 }}>
                    {selectedJob.crew_business_name && <>{selectedJob.crew_business_name}<br /></>}
                    {selectedJob.crew_phone && <>Phone: {selectedJob.crew_phone}<br /></>}
                    {selectedJob.crew_abn && <>ABN: {selectedJob.crew_abn}<br /></>}
                    {selectedJob.crew_gst_registered
                      ? <span style={{ color: '#67C6B4', fontWeight: 600 }}>Registered for GST</span>
                      : <span style={{ color: '#F87171', fontWeight: 600 }}>Not registered for GST</span>}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: 'rgba(255,255,255,.4)', marginBottom: 7 }}>BILLED TO (JOB OWNER)</div>
                  <div style={{ fontSize: 13.5, color: '#fff', fontWeight: 600 }}>{selectedJob.client_name}</div>
                  <div style={{ fontSize: 12.5, color: sub, lineHeight: 1.5 }}>
                    {selectedJob.client_business_name && <>{selectedJob.client_business_name}<br /></>}
                    {selectedJob.client_phone && <>Phone: {selectedJob.client_phone}<br /></>}
                    {selectedJob.client_abn && <>ABN: {selectedJob.client_abn}<br /></>}
                    {selectedJob.client_gst_registered
                      ? <span style={{ color: '#67C6B4', fontWeight: 600 }}>Registered for GST</span>
                      : <span style={{ color: '#F87171', fontWeight: 600 }}>Not registered for GST</span>}
                  </div>
                </div>
              </div>

              {/* Job details */}
              <div style={{ border: '1px solid rgba(255,255,255,.1)', borderRadius: 13, padding: '12px 16px', marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: 'rgba(255,255,255,.4)', marginBottom: 6 }}>JOB DETAILS</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>{selectedJob.title}</div>
                <div style={{ fontSize: 12.5, color: sub, marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Calendar className="w-[13px] h-[13px]" />{formatDate(selectedJob.date)}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><MapPin className="w-[13px] h-[13px]" />{selectedJob.location || 'N/A'}</span>
                </div>
              </div>

              {/* Line items */}
              <div style={{ border: '1px solid rgba(255,255,255,.1)', borderRadius: 13, padding: '6px 16px', marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,.65)' }}>{selectedJob.title} · {formatDate(selectedJob.date)}</span>
                  <span className="cd-mono" style={{ fontSize: 13, color: 'rgba(255,255,255,.85)', fontWeight: 500 }}>{formatCurrency(selectedJob.job_total)}</span>
                </div>

                {selectedJob.extra_payment_amount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,.65)' }}>Additional Payment · {selectedJob.extra_payment_reason || 'Extra services'}</span>
                    <span className="cd-mono" style={{ fontSize: 13, color: '#67C6B4', fontWeight: 500 }}>+{formatCurrency(selectedJob.extra_payment_amount)}</span>
                  </div>
                )}

                {selectedJob.gst_registered && selectedJob.job_gst > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,.65)' }}>GST on total amount above</span>
                    <span className="cd-mono" style={{ fontSize: 13, color: 'rgba(255,255,255,.85)', fontWeight: 500 }}>{formatCurrency(selectedJob.job_gst)}</span>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,.65)' }}>Shootzu Service Fee ({selectedJob.invoice_type === 'client' ? '5%' : '2%'})</span>
                  <span className="cd-mono" style={{ fontSize: 13, color: 'rgba(255,255,255,.85)', fontWeight: 500 }}>{selectedJob.invoice_type === 'crew' ? '−' : ''}{formatCurrency(selectedJob.platform_fee)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,.65)' }}>GST on Service Fee</span>
                  <span className="cd-mono" style={{ fontSize: 13, color: 'rgba(255,255,255,.85)', fontWeight: 500 }}>{formatCurrency(selectedJob.platform_fee_gst)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0' }}>
                  <span style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>
                    {selectedJob.invoice_type === 'client'
                      ? (selectedJob.payment_status === 'partially_paid' ? 'Amount paid' : 'Total paid')
                      : 'Net received'}
                  </span>
                  <span className="cd-display" style={{ fontSize: 13, color: '#67C6B4', fontWeight: 800 }}>
                    {selectedJob.invoice_type === 'client'
                      ? formatCurrency(selectedJob.total_paid)
                      : formatCurrency(selectedJob.net_received)}
                  </span>
                </div>
              </div>

              {/* Escrow / status reference */}
              {selectedJob.payment_status === 'paid_in_full' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 15px', borderRadius: 12, background: 'rgba(103,198,180,.07)', border: '1px solid rgba(103,198,180,.2)', marginBottom: 20 }}>
                  <CheckCircle className="w-4 h-4" style={{ color: '#67C6B4', flex: '0 0 auto' }} />
                  <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.7)' }}>
                    Released from escrow on <span style={{ color: '#fff' }}>{formatDate(selectedJob.payment_date)}</span>
                  </span>
                </div>
              )}
              {selectedJob.payment_status === 'partially_paid' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 15px', borderRadius: 12, background: 'rgba(180,83,9,.1)', border: '1px solid rgba(252,211,77,.2)', marginBottom: 20 }}>
                  <Clock className="w-4 h-4" style={{ color: '#FCD34D', flex: '0 0 auto' }} />
                  <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.7)' }}>
                    Remaining <span style={{ color: '#fff' }}>{formatCurrency((selectedJob.total_due || 0) - (selectedJob.total_paid || 0))}</span>
                    {selectedJob.second_payment_due_date && <> · due {formatDate(selectedJob.second_payment_due_date)}</>}
                  </span>
                </div>
              )}
              {selectedJob.payment_status === 'awaiting_payment' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 15px', borderRadius: 12, background: 'rgba(37,99,235,.1)', border: '1px solid rgba(96,165,250,.2)', marginBottom: 20 }}>
                  <Clock className="w-4 h-4" style={{ color: '#60A5FA', flex: '0 0 auto' }} />
                  <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.7)' }}>Awaiting payment via Shootzu escrow</span>
                </div>
              )}

              {/* Payment Timeline - on-demand Stripe lookup */}
              {selectedJob.transactions?.length > 0 && (
                <div style={{ border: '1px solid rgba(255,255,255,.1)', borderRadius: 13, padding: '14px 16px', marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: 'rgba(255,255,255,.4)' }}>PAYMENT TIMELINE</div>
                    {modalTransactions.length === 0 && !modalTransactionsLoading && (
                      <button
                        type="button"
                        onClick={() => fetchStripeTimeline(selectedJob.job_id, selectedJob.transactions)}
                        className="cd-btn"
                        style={{ padding: '6px 12px', fontSize: 11.5 }}
                      >
                        Load from Stripe
                      </button>
                    )}
                  </div>
                  <div>
                    {modalTransactionsLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: faint }}>
                        <div className="inline-block animate-spin rounded-full h-4 w-4" style={{ borderBottom: '2px solid #28C0D0' }}></div>
                        Fetching from Stripe...
                      </div>
                    ) : modalTransactions.length > 0 ? (
                      modalTransactions.map((txn, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '9px 0', borderBottom: idx === modalTransactions.length - 1 ? 'none' : '1px solid rgba(255,255,255,.07)' }}>
                          <div>
                            <p style={{ fontWeight: 500, color: 'rgba(255,255,255,.75)' }}>{txn.date_formatted}</p>
                            <p className="cd-mono" style={{ fontSize: 11, color: faint }}>{txn.charge_id || txn.id}</p>
                          </div>
                          <p className="cd-mono" style={{ fontWeight: 700, color: '#fff' }}>{formatCurrency(txn.amount)}</p>
                        </div>
                      ))
                    ) : (
                      <p style={{ fontSize: 11.5, color: faint }}>Click "Load from Stripe" to see exact transaction amounts</p>
                    )}
                  </div>
                </div>
              )}

              {/* Terms & footer */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 16, marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: 'rgba(255,255,255,.4)', marginBottom: 8 }}>TERMS &amp; CONDITIONS</div>
                <p style={{ fontSize: 11, color: faint, lineHeight: 1.6 }}>
                  Shootzu acts as a payment facilitator and is neither Business User nor Crew within this invoice.
                  By accepting a Job Offer, as Crew you acknowledge you are entering into a direct agreement with
                  the Business User. Your Crew Payment processed by Shootzu pays the invoice for the Job Offer.
                  Shootzu disclaims any responsibility for any damage or loss that you may suffer or incur as a
                  result of accepting a Job or hiring Crew, except for damage or loss, if any, that you might have
                  a right to recover if something that Shootzu has done or omitted to do breaches a consumer
                  guarantee set out in the Australian Consumer Law.
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginTop: 12, textAlign: 'center' }}>
                  Generated by Shootzu · www.shootzu.com · support@shootzu.com<br />
                  Shootzu is a division of Turbo Productions PTY LTD ABN 18 150 556 102
                </p>
              </div>

              {/* Actions — Download (keeps window.print handler) */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowInvoice(false)}
                  className="cd-btn"
                  style={{ flex: 1, padding: 13, fontSize: 14, background: 'transparent', border: '1px solid rgba(255,255,255,.2)' }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="cd-btn cd-btn-primary"
                  style={{ flex: 1.4, padding: 13, fontSize: 13.5 }}
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPrintable;
